<script setup>
import 'devextreme/ui/lookup'
import VuePdfEmbed from 'vue-pdf-embed'

import { ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { getI18n } from '@/plugins/i18n'
import { getLocalizedItems } from '@/plugins/i18n'

import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'

import { useNotify } from '@/composables/notify'
import { codeNameDisplayExpr } from '@/composables/label'

import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const { notifySuccess } = useNotify()

const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

await Promise.all([
  portalStore.fetchPrintModelsAsync(true),
  webSiteStore.fetchPrintSettingsAsync(true),
])

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  entity: {
    type: Number,
    default: null,
  },
  selectedRows: {
    type: Array,
    default: null,
  },
  filename: {
    type: String,
    default: 'report.pdf'
  },
  printFunction: {
    type: Function,
    default: null,
  },
  printPreviewFunction: {
    type: Function,
    default: null,
  },
  handleWarningMessages: {
    type: Function,
    default: null,
  },
})

const i18n = getI18n()

const emit = defineEmits(['update:visible', 'print'])
const popupVisible = useVModel(props, 'visible', emit)
const form = ref(null)
const isPreviewLoading = ref(true)
const previewContainer = ref(null)
const previewWidth = ref(0)
const previewBlobUrl = ref(null)
const previewPdfSource = ref(null)
let resizeObserver = null
let resizeDebounce = null

const entityPrintModels = portalStore.getLocalizedPrintModels().filter(i => i.entity === props.entity)
const entityPrintModelIds = entityPrintModels.map(i => i.id)

const getCurrentPrintSettings = () => structuredClone(webSiteStore.printSettings.find(i => i.entity === props.entity && i.is_default && entityPrintModelIds.includes(i.model)) || entityPrintModels[0]?.print_settings)
const currentPrintSettings = ref(getCurrentPrintSettings())
const getCurrentPrintOptions = () => entityPrintModels.find(i => i.id === currentPrintSettings.value.model)?.options

const getLookupOnFieldDataSource = (field) => {
  return {
    store: getLocalizedItems(getCurrentPrintOptions()[field]),
    sort: 'name',
  }
}

const getLookupOnField = (dataField, field) => {
  return {
    dataField: dataField, editorType: 'dxLookup',
    label: { text: i18n.t(`common.${field}`) },
    editorOptions: {
      dataSource: getLookupOnFieldDataSource(field),
      valueExpr: 'id',
      displayExpr: 'name',
      onValueChanged: e => refreshPreview(),
    },
  }
}

const formItems = [
  {
    itemType: 'group',
    cssClass: 'dx-form-group-no-background',
    colCount: 4,
    items: [
      {
        itemType: 'group',
        caption: i18n.t('common.printModel'),
        colSpan: 4,
        items: [
          {
            dataField: 'model', editorType: 'dxLookup',
            label: { text: i18n.t('common.printModel') },
            editorOptions: {
              dataSource: {
                store: entityPrintModels,
                sort: 'code',
              },
              valueExpr: 'id',
              displayExpr: codeNameDisplayExpr,
              onValueChanged: (e) => {
                e.cancel = true
                // Changing the print model will get the user or default print settings for that model
                const existingPrintSettings = webSiteStore.printSettings.find(i => i.model === e.value)
                if (existingPrintSettings) {
                  currentPrintSettings.value = structuredClone(existingPrintSettings)
                } else {
                  currentPrintSettings.value.model = e.value
                  currentPrintSettings.value.id = null
                  const defaultPrintModel = entityPrintModels.find(i => i.id === e.value)
                  currentPrintSettings.value.data = structuredClone(defaultPrintModel.print_settings?.data)
                  currentPrintSettings.value.portal_reference = defaultPrintModel.print_settings.id
                }
                const currentPrintOptions = getCurrentPrintOptions()
                Object.keys(currentPrintOptions).forEach(i => {
                  for (let j = 0; j < 3; j++) {
                    const propertyName = `data.column_${j + 1}.${i}`
                    const editor = form.value.instance?.getEditor(propertyName)
                    editor.instance().option('dataSource', getLookupOnFieldDataSource(i))
                    editor.instance().option('value', propertyName.split('.').reduce((o, i) => o?.[i], currentPrintSettings.value))
                  }
                })
              }
            }
          },
        ]
      },
      {
        itemType: 'group',
        caption: i18n.t('common.mainInfo'),
        items: [
          {
            dataField: 'header_option', editorType: 'dxSelectBox',
            editorOptions: {
              items: webSiteStore.getLocalizedPrintHeaderOptions().map(d => ({ id: d.id, text: d.name })),
              displayExpr: 'text',
              valueExpr: 'id',
              onValueChanged: e => refreshPreview(),
            },
            label: { text: i18n.t('common.printHeader') }
          },
          {
            dataField: 'has_codes', editorType: 'dxCheckBox',
            editorOptions: {
              onValueChanged: e => refreshPreview(),
            },
            label: { text: i18n.t('common.printCode', 2), location: 'left' }
          },
        ]
      },
      {
        itemType: 'group',
        caption: `${i18n.t('common.column')} 1`,
        items: [
          {
            dataField: 'data.column_1.is_printed', editorType: 'dxCheckBox',
            editorOptions: {
              onValueChanged: e => refreshPreview(),
            },
            label: { text: i18n.t('common.printed'), location: 'left'},
          },
          // ...Object.keys(getCurrentPrintOptions()).map(i => getLookupOnField(`data.column_1.${i}`, i)),
          getLookupOnField('data.column_1.currency', 'currency'),
          getLookupOnField('data.column_1.price', 'price'),
          getLookupOnField('data.column_1.vat', 'vat'),
          getLookupOnField('data.column_1.total', 'total'),
        ]
      },
      {
        itemType: 'group',
        caption: `${i18n.t('common.column')} 2`,
        items: [
          {
            dataField: 'data.column_2.is_printed', editorType: 'dxCheckBox',
            editorOptions: {
              onValueChanged: e => refreshPreview(),
            },
            label: { text: i18n.t('common.printed'), location: 'left'},
          },
          getLookupOnField('data.column_2.currency', 'currency'),
          getLookupOnField('data.column_2.price', 'price'),
          getLookupOnField('data.column_2.vat', 'vat'),
          getLookupOnField('data.column_2.total', 'total'),
        ]
      },
      {
        itemType: 'group',
        caption: `${i18n.t('common.column')} 3`,
        items: [
          {
            dataField: 'data.column_3.is_printed', editorType: 'dxCheckBox',
            editorOptions: {
              onValueChanged: e => refreshPreview(),
            },
            label: { text: i18n.t('common.printed'), location: 'left'},
          },
          getLookupOnField('data.column_3.currency', 'currency'),
          getLookupOnField('data.column_3.price', 'price'),
          getLookupOnField('data.column_3.vat', 'vat'),
          getLookupOnField('data.column_3.total', 'total'),
        ]
      },
    ]
  }
]

const cancelButtonOptions = ref({
  text: i18n.t('common.cancel'),
  onClick: () => { popupVisible.value = false }
})

const isPrinting = ref(false)
const printButtonOptions = ref({
  text: i18n.t('common.print'),
  onClick: async(e) => {
    try {
      await props.printFunction(props.filename, {
        filename: props.filename,
        ids: props.selectedRows.slice(0, 1).map(i => i.id),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        options: currentPrintSettings.value,
        printSettings: currentPrintSettings.value
      }, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
      popupVisible.value = false
    } catch (err) {
      if (props.handleWarningMessages) {
        props.handleWarningMessages(err)
      }
    }
  }
})

const saveButtonOptions = ref({
  text: i18n.t('common.setAsDefault'),
  isLoading: isPrinting,
  onClick: async(e) => {
    const printSettings = structuredClone(currentPrintSettings.value)
    //if the print settings already exists in webSiteStore.printSettings, update it, otherwise create it
    const userPrintSettings = webSiteStore.printSettings.find(i => i.id === printSettings.id)
    if (!userPrintSettings) {
      printSettings.id = null
    }
    printSettings.is_default = true
    await webSiteStore.upsertPrintSettingsAsync(printSettings)
    .then(async() => {
      notifySuccess(i18n.t('common.success'))
      currentPrintSettings.value = structuredClone(getCurrentPrintSettings())
    })
  }
})

const refreshPreview = async() => {
  if (previewBlobUrl.value) {
    window.URL.revokeObjectURL(previewBlobUrl.value)
    previewBlobUrl.value = null
  }
  previewPdfSource.value = null
  isPreviewLoading.value = true
  if (props.selectedRows.length) {
    try {
      const response = await props.printPreviewFunction({
        ids: props.selectedRows.slice(0, 1).map(i => i.id),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isPreview: true,
        printSettings: currentPrintSettings.value
      })
      const blob = response.data
      previewBlobUrl.value = URL.createObjectURL(blob)
      previewPdfSource.value = blob
    } catch (err) {
      if (props.handleWarningMessages) {
        props.handleWarningMessages(err)
      }
    } finally {
      isPreviewLoading.value = false
    }
  } else {
    isPreviewLoading.value = false
  }
}

const onShown = () => {
  currentPrintSettings.value = structuredClone(getCurrentPrintSettings())
  refreshPreview()

  resizeObserver = new ResizeObserver(([entry]) => {
    clearTimeout(resizeDebounce)
    resizeDebounce = setTimeout(() => {
      previewWidth.value = entry.contentRect.width * 0.9
    }, 200)
  })

  if (previewContainer.value) {
    resizeObserver.observe(previewContainer.value)
  }
}

const onHiding = () => {
  if (previewBlobUrl.value) {
    window.URL.revokeObjectURL(previewBlobUrl.value)
    previewBlobUrl.value = null
  }
  resizeObserver?.disconnect()
  popupVisible.value = false
}

</script>
<template>
  <dx-popup
    v-model:visible="popupVisible"
    height="80vh"
    width="90vw"
    :hide-on-outside-click="true"
    :title="i18n.t('common.print')"
    @shown="onShown"
    @hiding="onHiding"
  >
    <DxSplitter
      orientation="horizontal"
      height="100%"
    >
      <DxItem
        :collapsible="true"
      >
        <dx-form
          ref="form"
          class="q-mt-md"
          height="auto"
          :items="formItems"
          :form-data="currentPrintSettings"
        />
      </DxItem>
      <DxItem size="55%">
        <div
          ref="previewContainer"
          style="height: 100%; width: 100%;"
          class="overflow-auto bg-grey-3 q-pa-md flex flex-center"
        >
          <div
            v-if="isPreviewLoading"
            class="absolute-full flex flex-center bg-white"
            style="z-index: 1;"
          >
            <q-spinner
              color="primary"
              size="3rem"
            />
          </div>
          <div
            v-if="previewPdfSource"
            style="max-width: 100%;"
          >
            <vue-pdf-embed
              :source="previewBlobUrl"
              :width="previewWidth"
              style="max-width: 100%;"
            />
          </div>
          <div
            v-else-if="!isPreviewLoading"
          >
            <div class="flex flex-center text-grey-5 text-center">
              <div>
                <q-icon
                  name="mdi-file-image-outline"
                  size="4rem"
                />
                <div class="q-mt-sm">
                  {{ i18n.t('common.preview') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DxItem>
    </DxSplitter>
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="saveButtonOptions"
    />
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="printButtonOptions"
    />
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="cancelButtonOptions"
    />
  </dx-popup>
</template>
<style>
.vue-pdf-embed__page {
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
